var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getGlobalProperty } from "@faire/web/common/globals/getGlobalProperty";
import { isError } from "@faire/web/common/typescriptUtils";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { isRobot } from "@faire/web/common/user-agent/isRobot";
const isRawResponse = (r) => {
    if (!r || typeof r !== "object") {
        return false;
    }
    const obj = r;
    const isSuccessResponse = "data" in obj && typeof obj["status"] === "number" && "headers" in obj;
    const isFailureResponse = "error" in obj && isError(obj.error);
    return isSuccessResponse || isFailureResponse;
};
const getFaireApiNamespace = () => {
    const faire = getGlobalProperty("faire");
    if (!faire) {
        throw new Error("Faire global not found");
    }
    const api = faire.api || (faire.api = {});
    return api;
};
export const REQUEST_HANDLER_GLOBAL_KEY = "faire.api.handler";
export const getRequestHandler = () => getGlobalProperty(REQUEST_HANDLER_GLOBAL_KEY);
/**
 * Sets the global request handler, i.e. global.faire["api"]["handler"].
 * The request handler is later resolved via getRequestHandler (getGlobalProperty("faire.api.handler")),
 * so in a Zone context (i.e. SSR), this should be done using Zone.fork, setting "faire.api.handler".
 */
export const setRequestHandler = (handler) => {
    getFaireApiNamespace().handler = handler;
};
export const REQUEST_INTERCEPTOR_GLOBAL_KEY = "faire.api.requestInterceptors";
export const getRequestInterceptors = () => {
    var _a;
    const requestInterceptors = (_a = getGlobalProperty(REQUEST_INTERCEPTOR_GLOBAL_KEY)) !== null && _a !== void 0 ? _a : [];
    return Object.freeze(requestInterceptors.slice(0));
};
/**
 * Sets a global request interceptor.
 * The request interceptors are invoked before the request handler called.
 */
export const addRequestInterceptor = (interceptor) => {
    var _a;
    const interceptors = (getFaireApiNamespace().requestInterceptors =
        (_a = getFaireApiNamespace().requestInterceptors) !== null && _a !== void 0 ? _a : []);
    interceptors.push(interceptor);
};
export const RESPONSE_INTERCEPTOR_GLOBAL_KEY = "faire.api.responseInterceptors";
export const getResponseInterceptors = () => {
    var _a;
    const repsonseInterceptors = (_a = getGlobalProperty(RESPONSE_INTERCEPTOR_GLOBAL_KEY)) !== null && _a !== void 0 ? _a : [];
    return Object.freeze(repsonseInterceptors.slice(0));
};
/**
 * Sets a global request interceptor.
 * The request interceptors are invoked before the request handler called.
 */
export const addResponseInterceptor = (interceptor) => {
    var _a;
    const interceptors = (getFaireApiNamespace().responseInterceptors =
        (_a = getFaireApiNamespace().responseInterceptors) !== null && _a !== void 0 ? _a : []);
    interceptors.push(interceptor);
};
export function request(config) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        const requestHandler = getRequestHandler();
        if (requestHandler === undefined) {
            throw new Error("Error: request handler does not exist! Please use setRequestHandler() to set one.");
        }
        if (config.ignoreDuringPrerender && isPrerender()) {
            return undefined;
        }
        if (config.ignoreForRobots && isRobot()) {
            return undefined;
        }
        if ((config.method === "post" || config.method === "put") && !config.data) {
            config.data = {};
        }
        (_a = getRequestInterceptors()) === null || _a === void 0 ? void 0 : _a.forEach((i) => {
            config = i(config);
        });
        let response;
        try {
            response = yield requestHandler(config);
        }
        catch (e) {
            if (isError(e)) {
                response = {
                    error: e,
                };
            }
            else {
                response = {
                    error: new Error(`Unexpected error thrown while handling ${config.method} ${config.url}`),
                };
            }
        }
        if (isRawResponse(response)) {
            const interceptors = (_b = getResponseInterceptors()) !== null && _b !== void 0 ? _b : [];
            for (const i of interceptors) {
                response = yield i(config, response);
            }
            if ("error" in response) {
                throw response.error;
            }
            else if (config.rawResponse !== true) {
                response = response.data;
            }
        }
        return response;
    });
}
/**
 * Sets a global event interceptor.
 * The event interceptors are invoked when an event is tracked.
 */
export const addEventInterceptor = (interceptor) => {
    var _a;
    const interceptors = (getFaireEventsNamespace().interceptors =
        (_a = getFaireEventsNamespace().interceptors) !== null && _a !== void 0 ? _a : []);
    interceptors.push(interceptor);
};
const getFaireEventsNamespace = () => {
    const faire = getGlobalProperty("faire");
    if (!faire) {
        throw new Error("Faire global not found");
    }
    const events = faire.events || (faire.events = {});
    return events;
};
